import React, { useEffect, useMemo, useState } from 'react';
import { Button, ButtonGroup, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TodayRoundedIcon from '@mui/icons-material/TodayRounded';

function startOfWeekMonday(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const day = x.getDay();
    const diff = (day === 0 ? -6 : 1) - day;
    x.setDate(x.getDate() + diff);
    return x;
}

function addDays(d, n) {
    const x = new Date(d);
    x.setDate(x.getDate() + n);
    return x;
}

function dateKeyLocal(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, '0');
    const da = String(x.getDate()).padStart(2, '0');
    return `${y}-${m}-${da}`;
}

function formatMonthTitle(monthDate) {
    try {
        return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(monthDate);
    } catch {
        return 'Calendar';
    }
}

function formatWeekTitle(startDate) {
    try {
        const endDate = addDays(startDate, 6);
        const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
        const startFmt = new Intl.DateTimeFormat('en-GB', sameMonth ? { day: 'numeric' } : { day: 'numeric', month: 'short' });
        const endFmt = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
        return `${startFmt.format(startDate)}-${endFmt.format(endDate)}`;
    } catch {
        return 'Week View';
    }
}

function formatDayTitle(dayDate) {
    try {
        return new Intl.DateTimeFormat('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(dayDate);
    } catch {
        return 'Day View';
    }
}

function formatDeadlineShort(it) {
    const type = String(it?.type || '').toLowerCase();
    if (type === 'interim') {
        const n = it?.interim_number != null ? Number(it.interim_number) : null;
        return Number.isFinite(n) ? `INT ${n}` : 'INT';
    }
    return 'INI';
}

function formatShortTime(startIso, endIso, isAllDay) {
    if (isAllDay) return 'All day';
    try {
        const start = new Date(startIso);
        const end = new Date(endIso);
        const fmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
        return `${fmt.format(start)}-${fmt.format(end)}`;
    } catch {
        return 'Timed';
    }
}

function formatFullTime(startIso, endIso, isAllDay) {
    if (isAllDay) return 'All day';
    try {
        const start = new Date(startIso);
        const end = new Date(endIso);
        const fmt = new Intl.DateTimeFormat('en-GB', { hour: '2-digit', minute: '2-digit' });
        return `${fmt.format(start)}-${fmt.format(end)}`;
    } catch {
        return 'Timed';
    }
}

function clipText(value, max = 38) {
    const text = String(value || '').trim();
    if (!text) return '';
    if (text.length <= max) return text;
    return `${text.slice(0, Math.max(0, max - 1)).trimEnd()}...`;
}

function getInitials(value) {
    const parts = String(value || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'TM';
    return parts.slice(0, 2).map((part) => part.charAt(0).toUpperCase()).join('') || 'TM';
}

function getLocalMinutes(dateLike) {
    const d = new Date(dateLike);
    if (!Number.isFinite(d.getTime())) return null;
    return (d.getHours() * 60) + d.getMinutes();
}

const DOW = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const CALENDAR_HEADER_TINT = '#243242';
const CALENDAR_HEADER_TEXT = '#f8fafc';
const CALENDAR_HEADER_BORDER = 'rgba(255, 255, 255, 0.12)';
const COUNT_BADGE_BG = '#243242';
const COUNT_BADGE_FG = '#f8fafc';
const SECTION_META = {
    personal: {
        shortLabel: 'Mine',
        marker: 'MC',
        accent: '#2563eb',
        tint: 'rgba(37, 99, 235, 0.10)'
    },
    team: {
        shortLabel: 'Team Calendar',
        marker: 'TM',
        accent: '#0f766e',
        tint: 'rgba(15, 118, 110, 0.10)'
    },
    deadlines: {
        shortLabel: 'Due',
        marker: 'DL',
        accent: '#d97706',
        tint: 'rgba(217, 119, 6, 0.12)'
    }
};

export default function MonthCalendar({
    monthDate,
    viewMode,
    onChangeView,
    visibleSections,
    onToggleSectionVisibility,
    onPrevMonth,
    onNextMonth,
    onToday,

    workingDays,
    holidays,

    personalEvents,
    teamEvents,
    slaItems,

    statusChip,
    personalCalendarLabel,
    onSelectSiteVisit
}) {
    const [hoverPanel, setHoverPanel] = useState('');
    const [pinnedPanel, setPinnedPanel] = useState('');
    const [focusedControl, setFocusedControl] = useState('');
    const [calendarContainerFocused, setCalendarContainerFocused] = useState(false);

    const firstOfMonth = useMemo(() => {
        const d = new Date(monthDate);
        d.setDate(1);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [monthDate]);

    const gridStart = useMemo(() => startOfWeekMonday(firstOfMonth), [firstOfMonth]);
    const currentDay = useMemo(() => {
        const d = new Date(monthDate);
        d.setHours(0, 0, 0, 0);
        return d;
    }, [monthDate]);
    const view = String(viewMode || 'month').trim().toLowerCase();

    const visibleStart = useMemo(() => {
        if (view === 'day') return currentDay;
        if (view === 'week') return startOfWeekMonday(currentDay);
        return gridStart;
    }, [currentDay, gridStart, view]);

    const visibleDaysCount = view === 'day' ? 1 : (view === 'week' ? 7 : 42);
    const columns = view === 'day' ? 1 : 7;
    const gridDays = useMemo(() => Array.from({ length: visibleDaysCount }, (_, i) => addDays(visibleStart, i)), [visibleDaysCount, visibleStart]);
    const sectionVisibility = {
        personal: visibleSections?.personal !== false,
        team: visibleSections?.team !== false,
        due: visibleSections?.due !== false
    };

    const workingSet = useMemo(() => new Set(Array.isArray(workingDays) ? workingDays : [1, 2, 3, 4, 5]), [workingDays]);

    const holidayByDate = useMemo(() => {
        const map = new Map();
        for (const h of Array.isArray(holidays) ? holidays : []) {
            if (h?.date) map.set(String(h.date), h);
        }
        return map;
    }, [holidays]);

    const personalByDate = useMemo(() => {
        const map = new Map();
        for (const ev of Array.isArray(personalEvents) ? personalEvents : []) {
            const t = new Date(ev?.start);
            if (!Number.isFinite(t.getTime())) continue;
            const key = dateKeyLocal(t);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(ev);
        }
        for (const [, arr] of map) {
            arr.sort((a, b) => String(a.start).localeCompare(String(b.start)));
        }
        return map;
    }, [personalEvents]);

    const teamByDate = useMemo(() => {
        const map = new Map();
        for (const ev of Array.isArray(teamEvents) ? teamEvents : []) {
            const t = new Date(ev?.start);
            if (!Number.isFinite(t.getTime())) continue;
            const key = dateKeyLocal(t);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(ev);
        }
        for (const [, arr] of map) {
            arr.sort((a, b) => String(a.start).localeCompare(String(b.start)));
        }
        return map;
    }, [teamEvents]);

    const slaByDueDate = useMemo(() => {
        const map = new Map();
        for (const it of Array.isArray(slaItems) ? slaItems : []) {
            const due = String(it?.due_at || '').trim();
            if (!due) continue;
            const key = due.slice(0, 10);
            if (!key) continue;
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(it);
        }
        for (const [, arr] of map) {
            arr.sort((a, b) => String(a.reference).localeCompare(String(b.reference)));
        }
        return map;
    }, [slaItems]);

    const title = view === 'day'
        ? formatDayTitle(currentDay)
        : (view === 'week' ? formatWeekTitle(visibleStart) : formatMonthTitle(firstOfMonth));
    const rangeLabel = view === 'day' ? 'day' : (view === 'week' ? 'week' : 'month');
    const isMonthView = view === 'month';
    const isWeekView = view === 'week';
    const isDayView = view === 'day';
    const todayKey = dateKeyLocal(new Date());
    const activeDateKey = dateKeyLocal(currentDay);
    const isCurrentRangeToday = activeDateKey === todayKey;
    const dayTileMinHeight = isDayView ? 760 : (isWeekView ? 480 : 89);
    const sectionGap = isDayView ? 12 : (isWeekView ? 8 : 4);
    const gridGap = isMonthView ? 5 : 8;
    const tileHeaderPadding = isMonthView ? '2px 6px' : '8px 10px';
    const tileBodyPadding = isMonthView ? 5 : 8;
    const monthInlineLimit = 3;
    const dayGridStartHour = 6;
    const dayGridEndHour = 22;
    const dayGridSlotHeight = 48;
    const dayGridHours = Array.from({ length: dayGridEndHour - dayGridStartHour + 1 }, (_, i) => dayGridStartHour + i);
    const dayGridHeight = (dayGridEndHour - dayGridStartHour) * dayGridSlotHeight;

    useEffect(() => {
        function handlePointerDown(event) {
            const target = event.target;
            if (target && typeof target.closest === 'function') {
                const withinCalendar = target.closest('[data-calendar-panel-root="1"]');
                if (withinCalendar) return;
            }
            setPinnedPanel('');
            setHoverPanel('');
        }

        function handleEscape(event) {
            if (event.key === 'Escape') {
                setPinnedPanel('');
                setHoverPanel('');
            }
        }

        document.addEventListener('mousedown', handlePointerDown);
        document.addEventListener('touchstart', handlePointerDown, { passive: true });
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handlePointerDown);
            document.removeEventListener('touchstart', handlePointerDown);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const activePanel = pinnedPanel || hoverPanel;

    function siteVisitInteraction(item) {
        if (item?.source !== 'monsurv_site_visit' || typeof onSelectSiteVisit !== 'function') return {};
        return {
            role: 'button',
            tabIndex: 0,
            title: 'Manage Site Visit',
            onClick: () => onSelectSiteVisit(item),
            onKeyDown: (event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onSelectSiteVisit(item);
                }
            }
        };
    }

    function renderCountBadge(panelId, count) {
        const pinned = pinnedPanel === panelId;
        const focused = focusedControl === `count:${panelId}`;
        return (
            <Button
                type="button"
                disableRipple
                data-calendar-panel-root="1"
                onMouseEnter={() => {
                    if (!pinnedPanel) setHoverPanel(panelId);
                }}
                onMouseLeave={() => {
                    if (!pinnedPanel) setHoverPanel((prev) => (prev === panelId ? '' : prev));
                }}
                onFocus={() => {
                    setFocusedControl(`count:${panelId}`);
                    if (!pinnedPanel) setHoverPanel(panelId);
                }}
                onBlur={() => {
                    setFocusedControl((prev) => (prev === `count:${panelId}` ? '' : prev));
                    if (!pinnedPanel) setHoverPanel((prev) => (prev === panelId ? '' : prev));
                }}
                onClick={() => {
                    setPinnedPanel((prev) => (prev === panelId ? '' : panelId));
                    setHoverPanel('');
                }}
                style={{
                    minWidth: 32,
                    height: 28,
                    border: 0,
                    borderRadius: 999,
                    background: COUNT_BADGE_BG,
                    color: COUNT_BADGE_FG,
                    fontWeight: 800,
                    fontSize: 13,
                    cursor: 'pointer',
                    outline: 'none',
                    padding: '0 10px',
                    boxShadow: focused
                        ? '0 0 0 3px rgba(29, 78, 216, 0.28), 0 4px 12px rgba(36, 50, 66, 0.18)'
                        : (pinned
                            ? '0 0 0 2px rgba(37, 99, 235, 0.22), 0 4px 12px rgba(36, 50, 66, 0.18)'
                            : '0 4px 12px rgba(36, 50, 66, 0.18)')
                }}
                aria-label={pinned ? `Hide ${count} items` : `Show ${count} items`}
                aria-pressed={pinned}
                title={pinned ? `Hide ${count} items` : `Show ${count} items`}
            >
                {count}
            </Button>
        );
    }

    function renderPopover(panelId, sectionKey, children, { openUpwards = false } = {}) {
        if (activePanel !== panelId) return null;
        const meta = SECTION_META[sectionKey] || SECTION_META.personal;
        const pinned = pinnedPanel === panelId;
        return (
            <div
                data-calendar-panel-root="1"
                onMouseEnter={() => {
                    if (!pinnedPanel) setHoverPanel(panelId);
                }}
                onMouseLeave={() => {
                    if (!pinnedPanel) setHoverPanel('');
                }}
                style={{
                    position: 'absolute',
                    top: openUpwards ? 'auto' : 'calc(100% + 8px)',
                    bottom: openUpwards ? 'calc(100% + 8px)' : 'auto',
                    left: 0,
                    right: 0,
                    zIndex: 30,
                    padding: 10,
                    borderRadius: 12,
                    background: '#ffffff',
                    border: `1px solid ${meta.tint}`,
                    borderTop: `3px solid ${meta.accent}`,
                    boxShadow: '0 18px 38px rgba(15, 23, 42, 0.16)',
                    display: 'grid',
                    gap: 8
                }}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: meta.accent }}>
                        {meta.shortLabel} items
                    </div>
                    {pinned ? (
                        <Button
                            type="button"
                            size="small"
                            startIcon={<CloseRoundedIcon />}
                            onClick={() => setPinnedPanel('')}
                            style={{
                                border: 0,
                                background: 'transparent',
                                color: '#64748b',
                                fontSize: 11,
                                fontWeight: 700,
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            Close
                        </Button>
                    ) : null}
                </div>
                {children}
            </div>
        );
    }

    function renderSection({ panelId, sectionKey, count, single, popover, openUpwards = false, labelOverride = '', alwaysFlyout = false, trigger = null, helperText = '' }) {
        const meta = SECTION_META[sectionKey] || SECTION_META.personal;
        const headerLabel = String(labelOverride || meta.shortLabel || '').trim();
        return (
            <div style={{ position: 'relative' }} data-calendar-panel-root="1">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 22,
                                height: 18,
                                borderRadius: 999,
                                padding: '0 6px',
                                background: meta.tint,
                                color: meta.accent,
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.04em'
                            }}
                        >
                            {meta.marker}
                        </span>
                        <div style={{ fontSize: 11, color: 'var(--calendar-muted)', fontWeight: 700, letterSpacing: '0.02em' }}>
                            {headerLabel}
                        </div>
                    </div>
                    {count > 1 ? renderCountBadge(panelId, count) : null}
                </div>

                {count === 0 ? (
                    <div style={{ fontSize: 12, color: 'var(--calendar-muted)' }}>No items</div>
                ) : count === 1 && !alwaysFlyout ? (
                    single
                ) : (
                    <>
                        <div style={{ fontSize: 11, color: 'var(--calendar-muted)' }}>
                            {helperText || (count === 1 ? 'Hover or tap for details' : 'Hover or tap the count')}
                        </div>
                        {count === 1 ? (trigger || single) : null}
                        {renderPopover(panelId, sectionKey, popover, { openUpwards })}
                    </>
                )}
            </div>
        );
    }

    function renderDayLane({ sectionKey, label, items, emptyText = 'No entries' }) {
        const meta = SECTION_META[sectionKey] || SECTION_META.personal;
        const allDayItems = items.filter((item) => Boolean(item?.is_all_day));
        const timedItems = items.filter((item) => !item?.is_all_day);

        return (
            <div
                style={{
                    border: '1px solid var(--calendar-border)',
                    borderRadius: 14,
                    background: '#fff',
                    overflow: 'hidden',
                    minWidth: 0
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--calendar-border)',
                        background: meta.tint
                    }}
                >
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 26,
                                height: 20,
                                borderRadius: 999,
                                padding: '0 7px',
                                background: '#fff',
                                color: meta.accent,
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.04em'
                            }}
                        >
                            {meta.marker}
                        </span>
                        <div style={{ fontSize: 12, fontWeight: 800, color: meta.accent }}>{label}</div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--calendar-muted)', fontWeight: 700 }}>{items.length} item{items.length === 1 ? '' : 's'}</div>
                </div>

                <div style={{ padding: 12 }}>
                    {allDayItems.length ? (
                        <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
                            {allDayItems.map((item, idx) => (
                                <div
                                    key={`all-day-${sectionKey}-${idx}`}
                                    {...siteVisitInteraction(item)}
                                    style={{
                                        borderRadius: 10,
                                        padding: '8px 10px',
                                        background: 'rgba(15, 23, 42, 0.04)',
                                        borderLeft: `3px solid ${meta.accent}`,
                                        cursor: item?.source === 'monsurv_site_visit' ? 'pointer' : 'default'
                                    }}
                                >
                                    <div style={{ fontSize: 11, color: 'var(--calendar-muted)', fontWeight: 700 }}>All day</div>
                                    <div style={{ marginTop: 2, fontWeight: 650, color: 'var(--calendar-text)' }}>
                                        {sectionKey === 'team' ? `${item.owner || 'Team member'}${item.title ? ` · ${item.title}` : ''}` : (item.title || 'Untitled')}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : null}

                    <div style={{ display: 'grid', gridTemplateColumns: '56px 1fr', gap: 10, minHeight: dayGridHeight }}>
                        <div style={{ position: 'relative', height: dayGridHeight }}>
                            {dayGridHours.slice(0, -1).map((hour, idx) => (
                                <div
                                    key={`${sectionKey}-hour-${hour}`}
                                    style={{
                                        position: 'absolute',
                                        top: idx * dayGridSlotHeight,
                                        left: 0,
                                        right: 0,
                                        height: dayGridSlotHeight,
                                        fontSize: 11,
                                        color: 'var(--calendar-muted)',
                                        fontVariantNumeric: 'tabular-nums'
                                    }}
                                >
                                    {String(hour).padStart(2, '0')}:00
                                </div>
                            ))}
                        </div>

                        <div style={{ position: 'relative', height: dayGridHeight, borderLeft: '1px solid rgba(15, 23, 42, 0.06)' }}>
                            {dayGridHours.slice(0, -1).map((hour, idx) => (
                                <div
                                    key={`${sectionKey}-line-${hour}`}
                                    style={{
                                        position: 'absolute',
                                        top: idx * dayGridSlotHeight,
                                        left: 0,
                                        right: 0,
                                        height: dayGridSlotHeight,
                                        borderTop: idx === 0 ? '1px solid rgba(15, 23, 42, 0.08)' : '1px solid rgba(15, 23, 42, 0.06)'
                                    }}
                                />
                            ))}

                            {timedItems.length ? timedItems.map((item, idx) => {
                                const startMinutes = getLocalMinutes(item.start);
                                const endMinutes = getLocalMinutes(item.end);
                                const startBound = dayGridStartHour * 60;
                                const endBound = dayGridEndHour * 60;
                                const safeStart = Math.max(startBound, Math.min(endBound - 15, startMinutes ?? startBound));
                                const safeEnd = Math.max(safeStart + 30, Math.min(endBound, endMinutes ?? (safeStart + 30)));
                                const top = ((safeStart - startBound) / 60) * dayGridSlotHeight;
                                const height = Math.max(34, ((safeEnd - safeStart) / 60) * dayGridSlotHeight);
                                return (
                                    <div
                                        key={`${sectionKey}-timed-${idx}`}
                                        {...siteVisitInteraction(item)}
                                        style={{
                                            position: 'absolute',
                                            top,
                                            left: 10,
                                            right: 10,
                                            minHeight: height,
                                            borderRadius: 12,
                                            padding: '8px 10px',
                                            background: '#fff',
                                            border: `1px solid ${meta.tint}`,
                                            boxShadow: `inset 3px 0 0 ${meta.accent}`,
                                            overflow: 'hidden',
                                            cursor: item?.source === 'monsurv_site_visit' ? 'pointer' : 'default'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                            <div style={{ fontSize: 11, fontWeight: 800, color: meta.accent }}>
                                                {formatFullTime(item.start, item.end, item.is_all_day)}
                                            </div>
                                            {sectionKey === 'team' ? (
                                                <div
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        minWidth: 26,
                                                        height: 20,
                                                        borderRadius: 999,
                                                        padding: '0 8px',
                                                        background: meta.tint,
                                                        color: meta.accent,
                                                        fontSize: 10,
                                                        fontWeight: 800,
                                                        letterSpacing: '0.04em'
                                                    }}
                                                >
                                                    {getInitials(item.owner)}
                                                </div>
                                            ) : null}
                                        </div>
                                        <div style={{ marginTop: 4, fontWeight: 700, color: 'var(--calendar-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {sectionKey === 'team' ? clipText(item.owner || 'Team member', 36) : clipText(item.title || 'Untitled', 40)}
                                        </div>
                                        <div style={{ marginTop: 3, fontSize: 12, color: 'var(--calendar-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {sectionKey === 'team' ? clipText(item.title || 'Busy', 52) : clipText(item.title || 'Untitled', 52)}
                                        </div>
                                    </div>
                                );
                            }) : (
                                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: 'var(--calendar-muted)' }}>
                                    {emptyText}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    function renderDayDeadlineColumn(items) {
        return (
            <div
                style={{
                    border: '1px solid var(--calendar-border)',
                    borderRadius: 14,
                    background: '#fff',
                    overflow: 'hidden',
                    minWidth: 0
                }}
            >
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--calendar-border)',
                        background: SECTION_META.deadlines.tint
                    }}
                >
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <span
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: 26,
                                height: 20,
                                borderRadius: 999,
                                padding: '0 7px',
                                background: '#fff',
                                color: SECTION_META.deadlines.accent,
                                fontSize: 10,
                                fontWeight: 800,
                                letterSpacing: '0.04em'
                            }}
                        >
                            DL
                        </span>
                        <div style={{ fontSize: 12, fontWeight: 800, color: SECTION_META.deadlines.accent }}>Due</div>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--calendar-muted)', fontWeight: 700 }}>{items.length} item{items.length === 1 ? '' : 's'}</div>
                </div>

                <div style={{ padding: 12, display: 'grid', gap: 8, minHeight: dayGridHeight + 24 }}>
                    {items.length ? items.map((item) => (
                        <div
                            key={`${item.reference}-${item.report_instance}`}
                            style={{
                                border: '1px solid var(--calendar-border)',
                                borderRadius: 12,
                                padding: '10px 12px',
                                background: 'linear-gradient(180deg, rgba(217, 119, 6, 0.08), rgba(255, 255, 255, 0.96))',
                                boxShadow: 'inset 3px 0 0 #d97706'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                                <div style={{ fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {item.reference} · {formatDeadlineShort(item)}
                                </div>
                                {statusChip ? <div style={{ whiteSpace: 'nowrap' }}>{statusChip(item.status)}</div> : null}
                            </div>
                        </div>
                    )) : (
                        <div style={{ fontSize: 13, color: 'var(--calendar-muted)' }}>No deadlines due today</div>
                    )}
                </div>
            </div>
        );
    }

    function handleCalendarKeyDown(event) {
        if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
        const target = event.target;
        const tag = String(target?.tagName || '').toLowerCase();
        const isTextInput = tag === 'input' || tag === 'textarea' || tag === 'select' || Boolean(target?.isContentEditable);
        if (isTextInput) return;

        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            onPrevMonth?.();
            return;
        }
        if (event.key === 'ArrowRight') {
            event.preventDefault();
            onNextMonth?.();
            return;
        }
        if (event.key === 't' || event.key === 'T') {
            event.preventDefault();
            onToday?.();
        }
    }

    return (
        <div
            className="card"
            style={{
                marginBottom: 16,
                outline: 'none',
                boxShadow: calendarContainerFocused
                    ? '0 0 0 3px rgba(29, 78, 216, 0.28)'
                    : undefined
            }}
            data-calendar-shortcuts-root="1"
            tabIndex={0}
            role="region"
            aria-label="Calendar panel"
            onKeyDown={handleCalendarKeyDown}
            onFocus={() => setCalendarContainerFocused(true)}
            onBlur={(event) => {
                const related = event.relatedTarget;
                if (related && event.currentTarget.contains(related)) return;
                setCalendarContainerFocused(false);
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        minHeight: 44,
                        padding: '0 16px',
                        borderRadius: 14,
                        background: 'linear-gradient(135deg, rgba(36, 50, 66, 0.98), rgba(29, 78, 216, 0.92))',
                        color: '#f8fafc',
                        boxShadow: '0 12px 28px rgba(15, 23, 42, 0.16)',
                        border: '1px solid rgba(255, 255, 255, 0.12)'
                    }}
                >
                    <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: '-0.01em', color: 'inherit' }}>{title}</h3>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <div
                        role="group"
                        aria-label="Calendar section visibility"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '6px 10px',
                            borderRadius: 999,
                            background: 'rgba(36, 50, 66, 0.05)',
                            border: '1px solid rgba(36, 50, 66, 0.10)',
                            minHeight: 36,
                            flexWrap: 'wrap'
                        }}
                    >
                        {[
                            { key: 'personal', label: 'Personal Calendar' },
                            { key: 'team', label: 'Team Calendar' },
                            { key: 'due', label: 'Due Dates' }
                        ].map((option) => (
                            <label
                                key={option.key}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: '#243242',
                                    cursor: 'pointer',
                                    userSelect: 'none'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={sectionVisibility[option.key]}
                                    onChange={() => onToggleSectionVisibility?.(option.key)}
                                    onFocus={() => setFocusedControl(`toggle:${option.key}`)}
                                    onBlur={() => setFocusedControl((prev) => (prev === `toggle:${option.key}` ? '' : prev))}
                                    aria-label={`Toggle ${option.label}`}
                                    style={{
                                        margin: 0,
                                        outline: 'none',
                                        boxShadow: focusedControl === `toggle:${option.key}` ? '0 0 0 3px rgba(29, 78, 216, 0.32)' : 'none',
                                        borderRadius: 3
                                    }}
                                />
                                <span>{option.label}</span>
                            </label>
                        ))}
                    </div>
                    <ToggleButtonGroup
                        aria-label="Calendar view mode"
                        exclusive
                        value={view}
                        onChange={(_event, nextView) => {
                            if (nextView) onChangeView?.(nextView);
                        }}
                        style={{ display: 'inline-flex', alignItems: 'center', padding: 4, borderRadius: 999, background: 'rgba(36, 50, 66, 0.08)', border: '1px solid rgba(36, 50, 66, 0.12)' }}
                    >
                        {[
                            { key: 'day', label: 'Day' },
                            { key: 'week', label: 'Week' },
                            { key: 'month', label: 'Month' }
                        ].map((option) => {
                            const active = view === option.key;
                            return (
                                <ToggleButton
                                    key={option.key}
                                    value={option.key}
                                    onFocus={() => setFocusedControl(`view:${option.key}`)}
                                    onBlur={() => setFocusedControl((prev) => (prev === `view:${option.key}` ? '' : prev))}
                                    aria-pressed={active}
                                    aria-label={`Switch to ${option.label} view`}
                                    style={{
                                        border: 0,
                                        borderRadius: 999,
                                        padding: '7px 12px',
                                        background: active ? '#243242' : 'transparent',
                                        color: active ? '#f8fafc' : '#243242',
                                        fontWeight: 700,
                                        fontSize: 12,
                                        cursor: 'pointer',
                                        outline: 'none',
                                        boxShadow: focusedControl === `view:${option.key}` ? '0 0 0 3px rgba(29, 78, 216, 0.26)' : 'none'
                                    }}
                                >
                                    {option.label}
                                </ToggleButton>
                            );
                        })}
                    </ToggleButtonGroup>
                    <ButtonGroup
                        aria-label="Calendar date navigation"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            padding: 4,
                            borderRadius: 999,
                            background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(241, 245, 249, 0.96))',
                            border: '1px solid rgba(36, 50, 66, 0.14)',
                            boxShadow: '0 10px 24px rgba(15, 23, 42, 0.08)'
                        }}
                    >
                        <Button
                            startIcon={<ArrowBackRoundedIcon />}
                            onClick={onPrevMonth}
                            onFocus={() => setFocusedControl('nav:prev')}
                            onBlur={() => setFocusedControl((prev) => (prev === 'nav:prev' ? '' : prev))}
                            aria-label={`Previous ${rangeLabel}`}
                            aria-keyshortcuts="ArrowLeft"
                            style={{
                                border: '1px solid rgba(36, 50, 66, 0.14)',
                                borderRadius: 999,
                                minWidth: 72,
                                height: 38,
                                padding: '0 14px',
                                background: '#ffffff',
                                color: '#243242',
                                fontWeight: 700,
                                fontSize: 14,
                                cursor: 'pointer',
                                outline: 'none',
                                boxShadow: focusedControl === 'nav:prev'
                                    ? '0 0 0 3px rgba(29, 78, 216, 0.26), 0 1px 0 rgba(255, 255, 255, 0.8) inset'
                                    : '0 1px 0 rgba(255, 255, 255, 0.8) inset'
                            }}
                        >
                            Prev
                        </Button>
                        <Button
                            startIcon={<TodayRoundedIcon />}
                            onClick={onToday}
                            onFocus={() => setFocusedControl('nav:today')}
                            onBlur={() => setFocusedControl((prev) => (prev === 'nav:today' ? '' : prev))}
                            aria-label="Go to today"
                            aria-keyshortcuts="t"
                            style={{
                                border: isCurrentRangeToday ? '1px solid rgba(29, 78, 216, 0.28)' : '1px solid rgba(36, 50, 66, 0.14)',
                                borderRadius: 999,
                                minWidth: 86,
                                height: 38,
                                padding: '0 16px',
                                background: isCurrentRangeToday ? 'linear-gradient(135deg, #243242, #1d4ed8)' : '#ffffff',
                                color: isCurrentRangeToday ? '#f8fafc' : '#243242',
                                fontWeight: 800,
                                fontSize: 14,
                                letterSpacing: '0.01em',
                                cursor: 'pointer',
                                outline: 'none',
                                boxShadow: focusedControl === 'nav:today'
                                    ? '0 0 0 3px rgba(29, 78, 216, 0.32), 0 10px 20px rgba(29, 78, 216, 0.22)'
                                    : (isCurrentRangeToday ? '0 10px 20px rgba(29, 78, 216, 0.22)' : '0 1px 0 rgba(255, 255, 255, 0.8) inset')
                            }}
                        >
                            Today
                        </Button>
                        <Button
                            endIcon={<ArrowForwardRoundedIcon />}
                            onClick={onNextMonth}
                            onFocus={() => setFocusedControl('nav:next')}
                            onBlur={() => setFocusedControl((prev) => (prev === 'nav:next' ? '' : prev))}
                            aria-label={`Next ${rangeLabel}`}
                            aria-keyshortcuts="ArrowRight"
                            style={{
                                border: '1px solid rgba(36, 50, 66, 0.14)',
                                borderRadius: 999,
                                minWidth: 72,
                                height: 38,
                                padding: '0 14px',
                                background: '#ffffff',
                                color: '#243242',
                                fontWeight: 700,
                                fontSize: 14,
                                cursor: 'pointer',
                                outline: 'none',
                                boxShadow: focusedControl === 'nav:next'
                                    ? '0 0 0 3px rgba(29, 78, 216, 0.26), 0 1px 0 rgba(255, 255, 255, 0.8) inset'
                                    : '0 1px 0 rgba(255, 255, 255, 0.8) inset'
                            }}
                        >
                            Next
                        </Button>
                    </ButtonGroup>
                </div>
            </div>

            <div style={{ marginTop: 12 }}>
                <div style={{ display: 'grid', gridTemplateColumns: `repeat(${columns}, 1fr)`, gap: gridGap }}>
                    {(view !== 'day' ? DOW.slice(0, columns) : []).map((d) => (
                        <div
                            key={d}
                            style={{
                                fontSize: 12,
                                color: CALENDAR_HEADER_TEXT,
                                fontWeight: 700,
                                padding: isMonthView ? '6px 6px' : '8px 10px',
                                borderRadius: 10,
                                background: CALENDAR_HEADER_TINT,
                                border: `1px solid ${CALENDAR_HEADER_BORDER}`,
                                textAlign: 'center',
                                letterSpacing: '0.02em'
                            }}
                        >
                            {d}
                        </div>
                    ))}

                    {gridDays.map((d, index) => {
                        const key = dateKeyLocal(d);
                        const isToday = !isDayView && key === todayKey;
                        const rowIndex = Math.floor(index / columns);
                        const totalRows = Math.max(1, Math.ceil(gridDays.length / columns));
                        const openUpwards = totalRows > 1 && rowIndex >= (totalRows - 2);
                        const inMonth = d.getMonth() === firstOfMonth.getMonth();
                        const isoDow = (() => {
                            const dow = d.getDay();
                            return dow === 0 ? 7 : dow;
                        })();
                        const isWorking = workingSet.has(isoDow);

                        const holiday = holidayByDate.get(key);
                        const personal = sectionVisibility.personal ? (personalByDate.get(key) || []) : [];
                        const events = sectionVisibility.team ? (teamByDate.get(key) || []) : [];
                        const due = sectionVisibility.due ? (slaByDueDate.get(key) || []) : [];
                        const monthCombined = isMonthView ? [
                            ...due.map((item) => ({
                                kind: 'deadline',
                                id: `${item.reference}-${item.report_instance}`,
                                title: `${item.reference} · ${formatDeadlineShort(item)}`,
                                meta: '',
                                accent: '#d97706',
                                background: 'rgba(217, 119, 6, 0.08)',
                                status: item.status,
                                time: null
                            })),
                            ...personal.map((ev, idx) => ({
                                kind: 'personal',
                                id: `${ev.provider_event_id || ev.start}-${idx}`,
                                event: ev,
                                title: ev.title || (ev.is_auto_generated ? 'Reminder' : 'Calendar event'),
                                meta: formatShortTime(ev.start, ev.end, ev.is_all_day),
                                accent: '#2563eb',
                                background: 'rgba(37, 99, 235, 0.08)',
                                status: null,
                                time: getLocalMinutes(ev.start)
                            })),
                            ...events.map((ev, idx) => ({
                                kind: 'team',
                                id: `${ev.owner_user_id || ev.start}-${idx}`,
                                title: ev.owner || 'Team member',
                                meta: formatShortTime(ev.start, ev.end, ev.is_all_day),
                                accent: '#0f766e',
                                background: 'rgba(15, 118, 110, 0.08)',
                                status: null,
                                time: getLocalMinutes(ev.start)
                            }))
                        ] : [];
                        const personalInline = isDayView ? personal.slice(0, 6) : (isWeekView ? personal.slice(0, 2) : personal.slice(0, 1));
                        const teamInline = isDayView ? events.slice(0, 6) : (isWeekView ? events.slice(0, 2) : events.slice(0, 1));
                        const dueInline = isDayView ? due.slice(0, 6) : (isWeekView ? due.slice(0, 2) : due.slice(0, 1));

                        const bg = holiday
                            ? 'rgba(239, 68, 68, 0.06)'
                            : (!isWorking ? 'rgba(15, 23, 42, 0.03)' : 'var(--calendar-panel)');
                        const tileBorder = isToday ? '1px solid rgba(37, 99, 235, 0.42)' : '1px solid var(--calendar-border)';
                        const tileBoxShadow = isToday
                            ? '0 0 0 2px rgba(37, 99, 235, 0.12), 0 16px 30px rgba(37, 99, 235, 0.14)'
                            : 'none';
                        const tileBackground = isToday
                            ? 'linear-gradient(180deg, rgba(37, 99, 235, 0.05), rgba(255, 255, 255, 0.98))'
                            : bg;
                        const headerBackground = isToday
                            ? 'linear-gradient(135deg, #1d4ed8, #243242)'
                            : CALENDAR_HEADER_TINT;
                        const dateBadgeBackground = isToday ? '#ffffff' : 'transparent';
                        const dateBadgeColor = isToday ? '#1d4ed8' : CALENDAR_HEADER_TEXT;
                        const dateBadgeShadow = isToday ? '0 6px 16px rgba(15, 23, 42, 0.18)' : 'none';

                        return (
                            <div
                                key={d.toISOString()}
                                style={{
                                    border: tileBorder,
                                    borderRadius: 10,
                                    minWidth: 0,
                                    overflow: 'visible',
                                    background: tileBackground,
                                    color: 'var(--calendar-text)',
                                    opacity: inMonth ? 1 : 0.55,
                                    position: 'relative',
                                    zIndex: activePanel.startsWith(`${key}:`) ? 10 : 1,
                                    boxShadow: tileBoxShadow
                                }}
                            >
                                <div
                                    style={{
                                        padding: tileHeaderPadding,
                                        borderBottom: '1px solid var(--calendar-border)',
                                        background: headerBackground
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-start', gap: isMonthView ? 6 : 8, minWidth: 0, flex: 1 }}>
                                            <div
                                                style={{
                                                    fontWeight: 800,
                                                    fontVariantNumeric: 'tabular-nums',
                                                    color: dateBadgeColor,
                                                    width: isMonthView ? 20 : 28,
                                                    height: isMonthView ? 20 : 28,
                                                    borderRadius: 999,
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    background: dateBadgeBackground,
                                                    boxShadow: dateBadgeShadow,
                                                    letterSpacing: '0.01em',
                                                    fontSize: isMonthView ? 12 : 16,
                                                    lineHeight: 1,
                                                    flexShrink: 0
                                                }}
                                            >
                                                {d.getDate()}
                                            </div>
                                            {isToday ? (
                                                <div style={{ fontSize: isMonthView ? 9 : 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(248, 250, 252, 0.92)' }}>
                                                    Today
                                                </div>
                                            ) : null}
                                        </div>
                                        {holiday ? (
                                            <div style={{ fontSize: isMonthView ? 9 : 11, color: 'rgba(248, 250, 252, 0.88)', whiteSpace: 'nowrap' }}>
                                                {holiday.kind === 'closure' ? 'Closure' : 'Holiday'}
                                            </div>
                                        ) : null}
                                    </div>
                                    {holiday?.name ? (
                                        <div style={{ marginTop: isMonthView ? 0 : 4, fontSize: isMonthView ? 9 : 12, color: 'rgba(248, 250, 252, 0.88)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {holiday.name}
                                        </div>
                                    ) : null}
                                </div>

                                <div style={{ padding: tileBodyPadding, minHeight: dayTileMinHeight, display: 'grid', gap: sectionGap }}>
                                    {isDayView ? (
                                        <div
                                            style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'minmax(0, 1.1fr) minmax(0, 1.1fr) minmax(280px, 0.9fr)',
                                                gap: 12,
                                                alignItems: 'start'
                                            }}
                                        >
                                            {renderDayLane({
                                                sectionKey: 'personal',
                                                label: personalCalendarLabel || 'My Calendar',
                                                items: personal,
                                                emptyText: 'No personal entries'
                                            })}
                                            {renderDayLane({
                                                sectionKey: 'team',
                                                label: 'Team Calendar',
                                                items: events,
                                                emptyText: 'No shared team entries'
                                            })}
                                            {renderDayDeadlineColumn(due)}
                                        </div>
                                    ) : isMonthView ? (
                                        <div style={{ display: 'grid', gap: 1, alignContent: 'start' }}>
                                            {monthCombined.length ? monthCombined.slice(0, monthInlineLimit).map((entry) => (
                                                <div
                                                    key={entry.id}
                                                    {...siteVisitInteraction(entry.event)}
                                                    style={{
                                                        border: '1px solid var(--calendar-border)',
                                                        borderRadius: 5,
                                                        padding: '1px 4px',
                                                        background: entry.background,
                                                        boxShadow: `inset 3px 0 0 ${entry.accent}`,
                                                        minWidth: 0,
                                                        cursor: entry.event?.source === 'monsurv_site_visit' ? 'pointer' : 'default'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 3, minHeight: 9 }}>
                                                        <div style={{ fontSize: 10, lineHeight: 1.1, fontWeight: 700, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {clipText(entry.title, entry.kind === 'team' ? 20 : 24)}
                                                        </div>
                                                        {entry.status && statusChip ? (
                                                            <div style={{ transform: 'scale(0.72)', transformOrigin: 'right center', whiteSpace: 'nowrap' }}>
                                                                {statusChip(entry.status)}
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                    {entry.meta ? (
                                                        <div style={{ marginTop: 0, fontSize: 9, lineHeight: 1.05, color: 'var(--calendar-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                            {entry.meta}
                                                        </div>
                                                    ) : null}
                                                </div>
                                            )) : (
                                                <div style={{ fontSize: 10, color: 'var(--calendar-muted)' }}>No items</div>
                                            )}
                                            {monthCombined.length > monthInlineLimit ? (
                                                <div style={{ fontSize: 10, color: 'var(--calendar-muted)', fontWeight: 700 }}>
                                                    +{monthCombined.length - monthInlineLimit} more
                                                </div>
                                            ) : null}
                                        </div>
                                    ) : (
                                        <>
                                            {renderSection({
                                                panelId: `${key}:personal`,
                                                sectionKey: 'personal',
                                                labelOverride: isMonthView ? (personalCalendarLabel || 'My Calendar') : (personalCalendarLabel || 'My Calendar'),
                                                count: personal.length,
                                                openUpwards,
                                                single: personal.length ? (
                                                    <div style={{ display: 'grid', gap: 6 }}>
                                                        {personalInline.map((ev, idx) => (
                                                            <div
                                                                key={`${ev.provider_event_id || ev.start}-${idx}`}
                                                                {...siteVisitInteraction(ev)}
                                                                style={{
                                                                    border: '1px solid var(--calendar-border)',
                                                                    borderRadius: 10,
                                                                    padding: isDayView ? '8px 10px' : '6px 8px',
                                                                    background: 'var(--calendar-panel)',
                                                                    boxShadow: 'inset 3px 0 0 #2563eb',
                                                                    cursor: ev?.source === 'monsurv_site_visit' ? 'pointer' : 'default'
                                                                }}
                                                            >
                                                                <div style={{ fontWeight: 650, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                    {clipText(ev.title || (ev.is_auto_generated ? 'Reminder' : 'Calendar event'), isDayView ? 72 : 38)}
                                                                </div>
                                                                <div style={{ marginTop: 2, fontSize: 11, color: 'var(--calendar-muted)' }}>
                                                                    {isDayView ? formatFullTime(ev.start, ev.end, ev.is_all_day) : formatShortTime(ev.start, ev.end, ev.is_all_day)}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {!isDayView && personal.length > personalInline.length ? (
                                                            <div style={{ fontSize: 12, color: 'var(--calendar-muted)' }}>
                                                                +{personal.length - personalInline.length} more
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null,
                                                popover: personal.map((ev, idx) => (
                                                    <div
                                                        key={`${ev.provider_event_id || ev.start}-${idx}`}
                                                        {...siteVisitInteraction(ev)}
                                                        style={{
                                                            border: '1px solid var(--calendar-border)',
                                                            borderRadius: 10,
                                                            padding: '8px 10px',
                                                            background: 'linear-gradient(180deg, rgba(37, 99, 235, 0.06), rgba(255, 255, 255, 0.96))',
                                                            cursor: ev?.source === 'monsurv_site_visit' ? 'pointer' : 'default'
                                                        }}
                                                    >
                                                        <div style={{ fontWeight: 650, color: 'var(--calendar-text)' }}>
                                                            {clipText(ev.title || (ev.is_auto_generated ? 'Reminder' : 'Calendar event'), 54)}
                                                        </div>
                                                        <div style={{ marginTop: 3, fontSize: 12, color: 'var(--calendar-muted)' }}>
                                                            {formatShortTime(ev.start, ev.end, ev.is_all_day)}
                                                        </div>
                                                    </div>
                                                ))
                                            })}

                                            {renderSection({
                                                panelId: `${key}:team`,
                                                sectionKey: 'team',
                                                count: events.length,
                                                alwaysFlyout: isMonthView,
                                                openUpwards,
                                                helperText: isMonthView ? '' : '',
                                                trigger: isMonthView && events[0] ? (
                                                    <Button
                                                        type="button"
                                                        disableRipple
                                                        data-calendar-panel-root="1"
                                                        onMouseEnter={() => {
                                                            if (!pinnedPanel) setHoverPanel(`${key}:team`);
                                                        }}
                                                        onMouseLeave={() => {
                                                            if (!pinnedPanel) setHoverPanel((prev) => (prev === `${key}:team` ? '' : prev));
                                                        }}
                                                        onFocus={() => {
                                                            if (!pinnedPanel) setHoverPanel(`${key}:team`);
                                                        }}
                                                        onBlur={() => {
                                                            if (!pinnedPanel) setHoverPanel((prev) => (prev === `${key}:team` ? '' : prev));
                                                        }}
                                                        onClick={() => {
                                                            setPinnedPanel((prev) => (prev === `${key}:team` ? '' : `${key}:team`));
                                                            setHoverPanel('');
                                                        }}
                                                        style={{
                                                            width: '100%',
                                                            border: '1px solid var(--calendar-border)',
                                                            borderRadius: 10,
                                                            padding: '6px 8px',
                                                            background: 'var(--calendar-panel)',
                                                            boxShadow: 'inset 3px 0 0 #0f766e',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            gap: 8,
                                                            cursor: 'pointer',
                                                            textAlign: 'left'
                                                        }}
                                                        aria-label={`Show details for ${events[0].owner || 'team entry'}`}
                                                    >
                                                        <span
                                                            style={{
                                                                display: 'inline-flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                minWidth: 28,
                                                                height: 22,
                                                                borderRadius: 999,
                                                                padding: '0 8px',
                                                                background: 'rgba(15, 118, 110, 0.12)',
                                                                color: '#0f766e',
                                                                fontSize: 11,
                                                                fontWeight: 800,
                                                                letterSpacing: '0.04em'
                                                            }}
                                                        >
                                                            {getInitials(events[0].owner)}
                                                        </span>
                                                        <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 650, color: 'var(--calendar-text)' }}>
                                                            {formatShortTime(events[0].start, events[0].end, events[0].is_all_day)}
                                                        </span>
                                                    </Button>
                                                ) : null,
                                                single: events.length ? (
                                                    <div style={{ display: 'grid', gap: 6 }}>
                                                        {teamInline.map((ev, idx) => (
                                                            <div
                                                                key={`${ev.owner_user_id || ev.start}-${idx}`}
                                                                style={{
                                                                    border: '1px solid var(--calendar-border)',
                                                                    borderRadius: 10,
                                                                    padding: isDayView ? '8px 10px' : '6px 8px',
                                                                    background: 'var(--calendar-panel)',
                                                                    boxShadow: 'inset 3px 0 0 #0f766e'
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                                                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                                                                        <span
                                                                            style={{
                                                                                display: 'inline-flex',
                                                                                alignItems: 'center',
                                                                                justifyContent: 'center',
                                                                                minWidth: 28,
                                                                                height: 22,
                                                                                borderRadius: 999,
                                                                                padding: '0 8px',
                                                                                background: 'rgba(15, 118, 110, 0.12)',
                                                                                color: '#0f766e',
                                                                                fontSize: 11,
                                                                                fontWeight: 800,
                                                                                letterSpacing: '0.04em'
                                                                            }}
                                                                        >
                                                                            {getInitials(ev.owner)}
                                                                        </span>
                                                                        <div style={{ minWidth: 0 }}>
                                                                            <div style={{ fontWeight: 650, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                {clipText(ev.owner || 'Team member', isDayView ? 48 : 26)}
                                                                            </div>
                                                                            {isWeekView || isDayView ? (
                                                                                <div style={{ marginTop: 2, fontSize: 11, color: 'var(--calendar-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                                    {clipText(ev.title || 'Busy', isDayView ? 56 : 30)}
                                                                                </div>
                                                                            ) : null}
                                                                        </div>
                                                                    </div>
                                                                    <span style={{ marginLeft: 'auto', fontSize: 11, fontWeight: 650, color: 'var(--calendar-muted)', whiteSpace: 'nowrap' }}>
                                                                        {isDayView ? formatFullTime(ev.start, ev.end, ev.is_all_day) : formatShortTime(ev.start, ev.end, ev.is_all_day)}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {!isDayView && events.length > teamInline.length ? (
                                                            <div style={{ fontSize: 12, color: 'var(--calendar-muted)' }}>
                                                                +{events.length - teamInline.length} more
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null,
                                                popover: events.map((ev, idx) => (
                                                    <div
                                                        key={`${ev.owner_user_id || ev.start}-${idx}`}
                                                        style={{
                                                            border: '1px solid var(--calendar-border)',
                                                            borderRadius: 10,
                                                            padding: '8px 10px',
                                                            background: 'linear-gradient(180deg, rgba(15, 118, 110, 0.06), rgba(255, 255, 255, 0.96))'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                                                            <div style={{ fontWeight: 650, color: 'var(--calendar-text)' }}>
                                                                {clipText(ev.owner || 'Team member', 54)}
                                                            </div>
                                                            <div
                                                                style={{
                                                                    display: 'inline-flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: 28,
                                                                    height: 22,
                                                                    borderRadius: 999,
                                                                    padding: '0 8px',
                                                                    background: 'rgba(15, 118, 110, 0.12)',
                                                                    color: '#0f766e',
                                                                    fontSize: 11,
                                                                    fontWeight: 800,
                                                                    letterSpacing: '0.04em'
                                                                }}
                                                            >
                                                                {getInitials(ev.owner)}
                                                            </div>
                                                        </div>
                                                        <div style={{ marginTop: 4, fontSize: 12, color: 'var(--calendar-text)' }}>
                                                            {clipText(ev.title || 'Busy', 64)}
                                                        </div>
                                                        <div style={{ marginTop: 3, fontSize: 12, color: 'var(--calendar-muted)' }}>
                                                            {formatShortTime(ev.start, ev.end, ev.is_all_day)}
                                                        </div>
                                                    </div>
                                                ))
                                            })}

                                            {renderSection({
                                                panelId: `${key}:deadlines`,
                                                sectionKey: 'deadlines',
                                                count: due.length,
                                                openUpwards,
                                                single: due.length ? (
                                                    <div style={{ display: 'grid', gap: 6 }}>
                                                        {dueInline.map((item) => (
                                                            <div
                                                                key={`${item.reference}-${item.report_instance}`}
                                                                style={{
                                                                    border: '1px solid var(--calendar-border)',
                                                                    borderRadius: 10,
                                                                    padding: isDayView ? '8px 10px' : '6px 8px',
                                                                    background: 'var(--calendar-panel)',
                                                                    boxShadow: 'inset 3px 0 0 #d97706'
                                                                }}
                                                            >
                                                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                                                                    <div style={{ fontWeight: 650, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                        {item.reference} · {formatDeadlineShort(item)}
                                                                    </div>
                                                                    {statusChip ? <div style={{ whiteSpace: 'nowrap' }}>{statusChip(item.status)}</div> : null}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        {!isDayView && due.length > dueInline.length ? (
                                                            <div style={{ fontSize: 12, color: 'var(--calendar-muted)' }}>
                                                                +{due.length - dueInline.length} more
                                                            </div>
                                                        ) : null}
                                                    </div>
                                                ) : null,
                                                popover: due.map((it) => (
                                                    <div
                                                        key={`${it.reference}-${it.report_instance}`}
                                                        style={{
                                                            border: '1px solid var(--calendar-border)',
                                                            borderRadius: 10,
                                                            padding: '8px 10px',
                                                            background: 'linear-gradient(180deg, rgba(217, 119, 6, 0.08), rgba(255, 255, 255, 0.96))'
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'center' }}>
                                                            <div style={{ fontWeight: 650, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                                {it.reference} · {formatDeadlineShort(it)}
                                                            </div>
                                                            {statusChip ? <div style={{ whiteSpace: 'nowrap' }}>{statusChip(it.status)}</div> : null}
                                                        </div>
                                                    </div>
                                                ))
                                            })}
                                        </>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: 'var(--muted)' }}>
                Holidays/closures are highlighted. Non-working days are dimmed.
            </div>
            <div style={{ marginTop: 4, fontSize: 12, color: 'var(--muted)' }}>
                Keyboard shortcuts: Left/Right Arrow = previous/next range, T = today.
            </div>
        </div>
    );
}
